import React, { useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { any, object,  TypeOf, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { updateTimesheetFacility } from "../../redux/actions/timesheet.actions";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { green } from "@mui/material/colors";
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  createFilterOptions,
  Checkbox,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { storeStateType } from "../../redux/store";

dayjs.extend(customParseFormat);

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: any;
  action: string;
  filterData: any;
}

const formSchema = object({
  id: any(),
  facility: z.any(),
});

type FormInput = TypeOf<typeof formSchema>;

// describes the input data
type Category = {
  id: number;
  parent: any;
  facility_name: string;
  childCategories?: Category[];
};
interface Option {
  id: number;
  facility_name: string;
  depth: number;
  parentId: number | null;
  matchTerms: string[];
}

const toOptions = (
  category: Category,
  depth: number = 0,
  parentId: number | null = null
): Option[] => {
  const { id, facility_name, childCategories = [] } = category;
  const children = childCategories.flatMap((child) =>
    toOptions(child, depth + 1, id)
  );
  const option = {
    id,
    facility_name,
    depth,
    parentId,
    matchTerms: [facility_name].concat(
      children.map((obj) => obj.facility_name)
    ),
  };
  return [option].concat(children);
};

export function FacilityForm(props: Props) {
    const {handleCloseDialog} = props;
  const dispatch = useDispatch();
  const { timesheet, facilities, save } = useSelector(
    (state: storeStateType) => state.employeetimesheet
  );
  const loading = useSelector((state: storeStateType) => state.UI.loading);
  const {
    setValue,
    control,
    handleSubmit,
  } = useForm<FormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: { facility: null },
  });
  const [optionsList, setOptionsList] = React.useState<Option[]>([]);

  const handleClose = useCallback(() => {
    handleCloseDialog(false);
  }, [handleCloseDialog]);

  useEffect(() => {
    if (save) {
      handleClose();
    }
  }, [save,handleClose]);

  useEffect(() => {
    if (timesheet) {
      setValue("id", timesheet.id);
      setValue("facility", {
        id: timesheet.facility,
        facility_name: timesheet.facility_name,
      });
    }
  }, [timesheet,setValue]);

  React.useEffect(() => {
    if (facilities)
      setOptionsList(
        facilities.flatMap((category: any) => {
          return toOptions(category);
        })
      );
  }, [facilities]);

  const onFormSubmit: SubmitHandler<FormInput> = (data) => {
    const formData = new FormData();
    formData.append("facility", data.facility.id);
    formData.append("id", data.id);
    if (data.id) {
      dispatch<any>(
        updateTimesheetFacility(formData, data.id, props.filterData)
      );
    }
  };

  return (
    <Dialog open={props.isDialogOpened} onClose={handleClose} fullWidth>
      <DialogTitle>{props.action}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="companyform"
          name="companyform"
          noValidate
          onSubmit={handleSubmit(onFormSubmit)}
        >
          {props.isDialogOpened && optionsList && (
            <Controller
              control={control}
              name="facility"
              rules={{ required: true }}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  onChange={(event, item) => {
                    onChange(item);
                  }}
                  value={value}
                  options={optionsList}
                  //getOptionLabel={(item) => item.facility_name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(item) =>
                    item.facility_name ? item.facility_name : ""
                  }
                  /*  getOptionSelected={(option:any, value:any) =>
                                        value === undefined || value === "" || option.id === value.id
                                      } */

                  renderOption={(props, option, { selected, inputValue }) => {
                    const matches = match(option.facility_name, inputValue);
                    const parts = parse(option.facility_name, matches);
                    return (
                      <li {...props}>
                        <Checkbox
                          checked={selected}
                          sx={{ ml: 2 * option.depth }}
                        />
                        <div>
                          {parts.map((part: any, index: any) => (
                            <span
                              key={index}
                              style={{
                                fontWeight: part.highlight ? 700 : 400,
                              }}
                            >
                              {part.text}
                            </span>
                          ))}
                        </div>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Facility Name"
                    />
                  )}
                  filterOptions={createFilterOptions({
                    // join with some arbitrary separator to prevent matches across adjacent terms
                    stringify: (option: any) => option.matchTerms.join("//"),
                  })}
                />
              )}
            />
          )}

          <DialogActions>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button
                type="submit"
                form="companyform"
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                Save
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
            <Button
              onClick={handleClose}
              variant="contained"
              color="error"
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
